import type { ApiError } from '../api/client';
import type { DeliveryMode, DriverInput } from '../api/endpoints';
import { formatPhoneInput, isPhoneComplete, phoneToE164 } from './phone';

/**
 * Форма заведения курьера: состояние, проверка и тело запроса.
 *
 * Чистые функции отдельно от панели — по той же причине, что и маска телефона: ошибки
 * формы ловятся тестом, а не глазами на приёмке. Правила повторяют `DriverInputDto`
 * сервера и схему `DriverInput` контракта; клиентская проверка — подсказка человеку,
 * а не защита: сервер проверяет то же самое ещё раз.
 *
 * Пределы длины — ограничения схемы контракта, а не эксплуатационные параметры
 * (FR-417 их не касается): поменять их можно только вместе с контрактом.
 */

export const FULL_NAME_MAX = 200;
export const EXTERNAL_CODE_MAX = 64;

export const EMPLOYMENT_KINDS = ['staff', 'contracted'] as const;
export type Employment = (typeof EMPLOYMENT_KINDS)[number];

/**
 * Языки, которые предлагает форма. Контракт допускает и `en`, но у приложения курьера
 * две локали (FR-557): английский выбрать можно было бы, а увидеть — нет.
 */
export const DRIVER_LANGUAGES = ['uz', 'ru'] as const;
export type DriverLanguage = (typeof DRIVER_LANGUAGES)[number];

export interface DriverFormState {
  readonly full_name: string;
  /** Как в поле: `+998 93 380 71 77`. На сервер уходит без пробелов. */
  readonly phone: string;
  readonly employment: Employment | '';
  readonly external_code: string;
  readonly language: DriverLanguage;
  readonly modes: readonly DeliveryMode[];
  readonly is_active: boolean;
}

export type DriverField = 'full_name' | 'phone' | 'employment' | 'external_code' | 'modes';

/** Код ошибки поля — ключ в `messages.cabinet.drivers.errors`. */
export type DriverFieldError =
  | 'fullNameRequired'
  | 'fullNameTooLong'
  | 'phoneInvalid'
  | 'externalCodeTooLong'
  | 'modesRequired'
  | 'phoneTaken'
  | 'externalCodeTaken'
  | 'departmentForbidden';

export type DriverFormErrors = Partial<Record<DriverField, DriverFieldError>>;

/**
 * Пустая форма. Отдел у пользователя один — он и отмечен: выбирать не из чего, а
 * пустой список сервер отклонил бы. Отделов несколько — не отмечен ни один: курьер в
 * чужом по ошибке отделе получит чужие заказы, пусть человек выберет сам.
 */
export function emptyDriverForm(departments: readonly DeliveryMode[]): DriverFormState {
  return {
    full_name: '',
    phone: formatPhoneInput(''),
    employment: '',
    external_code: '',
    language: 'uz',
    modes: departments.length === 1 ? [...departments] : [],
    is_active: true,
  };
}

export function validateDriverForm(form: DriverFormState): DriverFormErrors {
  const errors: DriverFormErrors = {};
  const name = form.full_name.trim();

  if (name === '') {
    errors.full_name = 'fullNameRequired';
  } else if (name.length > FULL_NAME_MAX) {
    errors.full_name = 'fullNameTooLong';
  }

  if (!isPhoneComplete(form.phone)) {
    errors.phone = 'phoneInvalid';
  }

  if (form.external_code.trim().length > EXTERNAL_CODE_MAX) {
    errors.external_code = 'externalCodeTooLong';
  }

  if (form.modes.length === 0) {
    errors.modes = 'modesRequired';
  }

  return errors;
}

export function hasErrors(errors: DriverFormErrors): boolean {
  return Object.keys(errors).length > 0;
}

/** Тело `POST /web/drivers`. Необязательное пустое поле не отправляется вовсе. */
export function toDriverInput(form: DriverFormState): DriverInput {
  const code = form.external_code.trim();

  return {
    full_name: form.full_name.trim(),
    phone: phoneToE164(form.phone),
    ...(form.employment === '' ? {} : { employment: form.employment }),
    ...(code === '' ? {} : { external_code: code }),
    language: form.language,
    is_active: form.is_active,
    modes: [...form.modes],
  };
}

/** Поля, которые сервер называет в `errors[].field` и которые есть в форме. */
const SERVER_FIELDS: Readonly<Record<string, DriverField>> = {
  full_name: 'full_name',
  phone: 'phone',
  employment: 'employment',
  external_code: 'external_code',
  modes: 'modes',
};

/** Чем подсветить поле, если сервер указал его без собственного кода причины. */
const FIELD_FALLBACK: Readonly<Record<DriverField, DriverFieldError | undefined>> = {
  full_name: 'fullNameRequired',
  phone: 'phoneInvalid',
  employment: undefined,
  external_code: 'externalCodeTooLong',
  modes: 'modesRequired',
};

/**
 * Отказ сервера → ошибки по полям.
 *
 * Код `code` не зависит от языка (разд. 3.3), поэтому сопоставление идёт по нему, а
 * подпись берётся из ресурсов кабинета. Остаток — `general`: его форма показывает
 * общим блоком с кодом для поддержки.
 */
export function errorsFromProblem(error: ApiError): {
  readonly fields: DriverFormErrors;
  readonly general: boolean;
} {
  switch (error.code) {
    case 'phone_taken':
      return { fields: { phone: 'phoneTaken' }, general: false };
    case 'external_code_taken':
    case 'external_code_taken_driver':
      return { fields: { external_code: 'externalCodeTaken' }, general: false };
    case 'department_forbidden':
      return { fields: { modes: 'departmentForbidden' }, general: false };
    default:
      break;
  }

  const fields: DriverFormErrors = {};

  for (const item of error.problem.errors ?? []) {
    const field = SERVER_FIELDS[item.field.split('.')[0] ?? ''];
    const code = field === undefined ? undefined : FIELD_FALLBACK[field];

    if (field !== undefined && code !== undefined) {
      fields[field] = code;
    }
  }

  return { fields, general: true };
}
